import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { formatDateOnlyShort } from '@/modules/service-desk/shared/application-utils';
import { readBankInfo } from '@/modules/service-desk/shared/bank-info';
import {
  actionsColumn,
  appliedDateColumn,
  departmentColumn,
  DepartmentOption,
  NAME_ON_CARD,
  statusColumn,
  textColumn,
} from '@/modules/service-desk/shared/components/column-helpers';
import { StatusSelectCell } from '@/modules/service-desk/shared/components/status-select-cell';
import {
  canArchiveMetlife,
  canCancelMetlife,
  canEditMetlife,
  canExcludeMetlife,
  canReviewerEditMetlife,
  genderLabelKey,
  isMetlifeStatusLocked,
  METLIFE_GENDERS,
  METLIFE_APPLICATION_TYPES,
  METLIFE_FILTER_STATUSES,
  METLIFE_SELECTABLE_STATUSES,
  typeLabel,
} from '../../utils/metlife-rules';
import { MetlifeApplication, MetlifeStatus } from '../../types/metlife.types';

type Translate = (key: string) => string;

/** Every MetLife status (the filter list holds them all). */
const STATUSES = METLIFE_FILTER_STATUSES.map((option) => option.value);

const typeColumn = (t: Translate) =>
  textColumn<MetlifeApplication>(
    t,
    'TypeOfApplication',
    'TYPE_OF_APPLICATION',
    (row) => typeLabel(row.TypeOfApplication),
    {
      filter: {
        type: 'options',
        options: METLIFE_APPLICATION_TYPES.map((type) => ({
          value: type.value,
          label: type.label,
        })),
      },
    }
  );

const mobileColumn = (t: Translate) =>
  textColumn<MetlifeApplication>(t, 'MobileNumber', 'MOBILE_NUMBER', (row) => row.MobileNumber);

/** Date only (birth date): shown by its date part, never shifted. */
const dateOfBirthColumn = (t: Translate): ColumnDef<MetlifeApplication> => ({
  id: 'DateOfBirth',
  meta: { filter: { type: 'date' } },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('DATE_OF_BIRTH')} />,
  accessorFn: (row) => formatDateOnlyShort(row.DateOfBirth),
});

/** Legacy "Branch / A/C" and routing columns; both live in `BankInfoJson`, so no sort or filter. */
const bankColumn = (t: Translate) =>
  textColumn<MetlifeApplication>(
    t,
    'BankAccount',
    'BANK_ACCOUNT',
    (row) => {
      const bank = readBankInfo(row.BankInfoJson);
      return [bank.BranchName, bank.AccountNumber].filter(Boolean).join(' / ');
    },
    { filter: false, sortable: false }
  );

const routingColumn = (t: Translate) =>
  textColumn<MetlifeApplication>(
    t,
    'RoutingNumber',
    'ROUTING_NO',
    (row) => readBankInfo(row.BankInfoJson).RoutingNumber,
    { filter: false, sortable: false }
  );

/** Employee name with the email under it, as in the legacy requests list. */
const nameColumn = (t: Translate): ColumnDef<MetlifeApplication> => ({
  id: 'Name',
  meta: { filter: { type: 'text' }, ...NAME_ON_CARD },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('NAME')} />,
  accessorFn: (row) => row.Name || '-',
  cell: ({ row }) => <UserNameCell name={row.original.Name} email={row.original.Email} />,
});

const genderColumn = (t: Translate) =>
  textColumn<MetlifeApplication>(
    t,
    'Gender',
    'GENDER',
    (row) => (row.Gender ? t(genderLabelKey(row.Gender)) : ''),
    {
      filter: {
        type: 'options',
        options: METLIFE_GENDERS.map((gender) => ({
          value: gender.value,
          label: t(gender.labelKey),
        })),
      },
    }
  );

interface MyColumnsProps {
  t: Translate;
  canApply: boolean;
  canWithdraw: boolean;
  canCancel: boolean;
  onViewDetails: (application: MetlifeApplication) => void;
  onEdit: (application: MetlifeApplication) => void;
  onWithdraw: (application: MetlifeApplication) => void;
  onCancelPolicy: (application: MetlifeApplication) => void;
}

export const createMyMetlifeColumns = ({
  t,
  canApply,
  canWithdraw,
  canCancel,
  onViewDetails,
  onEdit,
  onWithdraw,
  onCancelPolicy,
}: MyColumnsProps): ColumnDef<MetlifeApplication>[] => [
  appliedDateColumn<MetlifeApplication>(t, 'REQUEST_DATE'),
  typeColumn(t),
  textColumn<MetlifeApplication>(
    t,
    'PolicyApplicantName',
    'POLICY_APPLICANT',
    (row) => row.PolicyApplicantName
  ),
  dateOfBirthColumn(t),
  mobileColumn(t),
  bankColumn(t),
  routingColumn(t),
  textColumn<MetlifeApplication>(t, 'MetLifeIdNumber', 'METLIFE_ID', (row) => row.MetLifeIdNumber),
  statusColumn<MetlifeApplication>(t, STATUSES),
  actionsColumn<MetlifeApplication>(t, (application) => [
    { label: t('VIEW_DETAILS'), onSelect: () => onViewDetails(application) },
    {
      label: t('EDIT'),
      onSelect: () => onEdit(application),
      hidden: !canApply || !canEditMetlife(application),
    },
    {
      label: t('WITHDRAW_METLIFE'),
      onSelect: () => onWithdraw(application),
      hidden: !canWithdraw || application.Status !== 'Pending',
      destructive: true,
    },
    {
      label: t('CANCEL_METLIFE'),
      onSelect: () => onCancelPolicy(application),
      hidden: !canCancel || !canCancelMetlife(application),
      destructive: true,
    },
  ]),
];

interface RequestColumnsProps {
  t: Translate;
  /** The departments the reviewer may see, for the Department column and its filter. */
  departments: readonly DepartmentOption[];
  canUpdateStatus: boolean;
  canExclude: boolean;
  /** The application whose status change is being saved; its select is disabled meanwhile. */
  savingId?: string;
  onViewDetails: (application: MetlifeApplication) => void;
  onEdit: (application: MetlifeApplication) => void;
  onChangeStatus: (application: MetlifeApplication, status: MetlifeStatus) => void;
  onArchive: (application: MetlifeApplication) => void;
  onExclude: (application: MetlifeApplication) => void;
}

export const createMetlifeRequestColumns = ({
  t,
  departments,
  canUpdateStatus,
  canExclude,
  savingId,
  onViewDetails,
  onEdit,
  onChangeStatus,
  onArchive,
  onExclude,
}: RequestColumnsProps): ColumnDef<MetlifeApplication>[] => [
  appliedDateColumn<MetlifeApplication>(t, 'REQUESTED_ON'),
  textColumn<MetlifeApplication>(t, 'EmployeeId', 'EMPLOYEE_ID', (row) => row.EmployeeId),
  nameColumn(t),
  departmentColumn<MetlifeApplication>(
    t,
    departments,
    (row) => row.RequestDepartmentName || row.BusinessUnitName
  ),
  mobileColumn(t),
  textColumn<MetlifeApplication>(
    t,
    'PolicyApplicantName',
    'POLICY_APPLICANT',
    (row) => row.PolicyApplicantName
  ),
  genderColumn(t),
  typeColumn(t),
  textColumn<MetlifeApplication>(t, 'MetLifeIdNumber', 'METLIFE_ID', (row) => row.MetLifeIdNumber),
  statusColumn<MetlifeApplication>(t, STATUSES, ({ row }) => (
    <StatusSelectCell
      status={row.original.Status}
      options={METLIFE_SELECTABLE_STATUSES}
      locked={!canUpdateStatus || isMetlifeStatusLocked(row.original)}
      disabled={row.original.ItemId === savingId}
      onChange={(status) => onChangeStatus(row.original, status as MetlifeStatus)}
    />
  )),
  actionsColumn<MetlifeApplication>(t, (application) => [
    { label: t('VIEW_DETAILS'), onSelect: () => onViewDetails(application) },
    {
      label: t('EDIT'),
      onSelect: () => onEdit(application),
      hidden: !canUpdateStatus || !canReviewerEditMetlife(application),
    },
    {
      label: t('ARCHIVE'),
      onSelect: () => onArchive(application),
      hidden: !canUpdateStatus || !canArchiveMetlife(application),
    },
    {
      label: t('EXCLUDE'),
      onSelect: () => onExclude(application),
      hidden: !canExclude || !canExcludeMetlife(application),
      destructive: true,
    },
  ]),
];
